import { Trash, CurrencyInr, CheckCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const fmtInr = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * Collect-Payment dialog. Records a partial/full payment against a candidate's outstanding charge.
 * Presentational — parent owns form state and submit handler.
 */
export const CollectPaymentDialog = ({
  open, onOpenChange,
  row,
  amount, setAmount,
  date, setDate,
  note, setNote,
  onSubmit,
  onRemovePayment,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0F3622] border-2 border-[#A3E635] shadow-[6px_6px_0px_0px_rgba(163,230,53,0.4)] sm:max-w-[520px]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
          <CurrencyInr size={28} weight="duotone" className="text-[#A3E635]" />
          Collect Payment
        </DialogTitle>
        <DialogDescription className="sr-only">Record a payment received from the candidate</DialogDescription>
      </DialogHeader>
      {row && (
        <div className="space-y-4">
          <div className="bg-[#0B2818] border-2 border-[#143E29] rounded-md p-3 text-sm">
            <p className="text-white font-bold">{row.candidate_name} <span className="text-white font-normal">– {row.company_name}</span></p>
            <p className="text-white mt-1">
              Charge: <span className="font-bold">{fmtInr(row.consultancy_charge)}</span>
              {' · '}Paid: <span className="font-bold">{fmtInr(row.total_paid)}</span>
              {' · '}Balance: <span className="font-bold text-[#FDE047]">{fmtInr(row.balance)}</span>
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-white">Amount (₹)</Label>
              <Input
                data-testid="payment-amount-input"
                type="number" min="0.01" step="0.01" required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-white">Collected Date</Label>
              <Input
                data-testid="payment-date-input"
                type="date" required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-white">Note (optional)</Label>
              <Input
                data-testid="payment-note-input"
                type="text" value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="UPI / Cash / Bank ref"
                className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black"
              />
            </div>

            {row.payments && row.payments.length > 0 && (
              <div className="border-t-2 border-[#143E29] pt-3">
                <p className="text-xs font-bold uppercase tracking-wider text-white mb-2">Previous Payments</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {row.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-[#0B2818] border-2 border-[#143E29] rounded-md px-3 py-2 text-xs">
                      <div className="text-white">
                        <span className="font-bold">{fmtInr(p.amount)}</span>
                        <span className="ml-2">on {p.collected_date}</span>
                        {p.note && <span className="ml-2 text-white">– {p.note}</span>}
                      </div>
                      <button
                        type="button"
                        data-testid={`remove-payment-${p.id}`}
                        onClick={() => onRemovePayment(row.candidate_id, p.id)}
                        className="p-1 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 transition-all"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              type="submit"
              data-testid="submit-payment"
              className="w-full bg-[#A3E635] text-black border-2 border-[#A3E635] rounded-md font-bold px-4 py-3 shadow-[3px_3px_0px_0px_rgba(163,230,53,0.5)] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(163,230,53,0.7)] transition-all"
            >
              <CheckCircle size={20} weight="bold" className="mr-2" />
              Record Payment
            </Button>
          </form>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
